/**
 * Reparaturauftraege (Fachmodul "repairs").
 *
 * Liste der Vorgänge mit Status- und Typfilter, Annahme und Bearbeitung
 * im Dialog. Positionen sind Artikel — Arbeitszeit ist ein Artikel ohne
 * Bestandsführung (Stundensätze). Kostenvoranschlag und Rechnung
 * entstehen als normale Belege und werden hier nur verknüpft.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  formatAmount,
  formatDate,
  formatQuantity,
  lineAmount,
  REPAIR_EDITABLE_STATUSES,
  REPAIR_STATUS_LABELS,
  REPAIR_STATUSES,
  REPAIR_TRANSITIONS,
  REPAIR_TYPE_LABELS,
  type RepairStatus,
  type RepairType,
} from '@erp/shared';

import { ApiError, api, withQuery } from '../api/client';
import type { ListResponse, Partner } from '../api/types';
import { useAuth } from '../auth';
import { ItemPicker } from '../components/ItemPicker';
import { SearchSelect } from '../components/SearchSelect';
import { PageHeader } from '../components/Layout';
import { useToast } from '../components/toast';
import {
  Alert,
  Badge,
  Dialog,
  EmptyState,
  Field,
  LoadingBlock,
  NumericInput,
  Select,
  TextArea,
  TextInput,
} from '../components/ui';

type RepairRow = {
  id: string;
  number: string;
  repairType: RepairType;
  partnerId: string | null;
  partnerName: string | null;
  deviceName: string;
  serialNumber: string | null;
  faultDescription: string;
  receivedDate: string;
  status: RepairStatus;
  quoteDocumentId: string | null;
  invoiceDocumentId: string | null;
};

type RepairLineRow = {
  id: string;
  itemId: string;
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  taxCode: string;
  netAmount: number;
};

type DocumentSummary = {
  id: string;
  type: string;
  number: string | null;
  lifecycle: string;
  grossTotal: number;
} | null;

type RepairDetail = {
  repair: {
    id: string;
    number: string;
    repairType: RepairType;
    partnerId: string | null;
    deviceName: string;
    serialNumber: string | null;
    deviceNotes: string | null;
    faultDescription: string;
    receivedDate: string;
    status: RepairStatus;
    approvalNote: string | null;
    completedAt: string | null;
    consumptionBooked: boolean;
    quoteDocumentId: string | null;
    invoiceDocumentId: string | null;
  };
  lines: RepairLineRow[];
  totals: { netTotal: number; taxTotal: number; grossTotal: number };
  partner: { id: string; name: string; number: string } | null;
  quoteDocument: DocumentSummary;
  invoiceDocument: DocumentSummary;
};

function statusBadge(status: RepairStatus) {
  const kind =
    status === 'received'
      ? 'warning'
      : status === 'quote_sent' || status === 'in_repair'
        ? 'accent'
        : status === 'approved' || status === 'completed'
          ? 'success'
          : status === 'declined'
            ? 'danger'
            : 'neutral';
  return <Badge kind={kind}>{REPAIR_STATUS_LABELS[status]}</Badge>;
}

export function RepairsPage() {
  const { can } = useAuth();
  const [statusFilter, setStatusFilter] = useState<RepairStatus | ''>('');
  const [typeFilter, setTypeFilter] = useState<RepairType | ''>('');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['repairs', statusFilter, typeFilter, search],
    queryFn: () =>
      api.get<ListResponse<RepairRow>>(
        withQuery('/api/repairs', {
          status: statusFilter,
          repairType: typeFilter,
          q: search,
          limit: 100,
        }),
      ),
  });

  return (
    <>
      <PageHeader
        title="Reparaturen"
        actions={
          can('repairs:write') ? (
            <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
              Gerät annehmen
            </button>
          ) : undefined
        }
      />

      <div className="row" style={{ gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        <button
          type="button"
          className={`btn btn-sm${statusFilter === '' ? ' btn-primary' : ''}`}
          onClick={() => setStatusFilter('')}
        >
          Alle
        </button>
        {REPAIR_STATUSES.map((status) => (
          <button
            key={status}
            type="button"
            className={`btn btn-sm${statusFilter === status ? ' btn-primary' : ''}`}
            onClick={() => setStatusFilter(status)}
          >
            {REPAIR_STATUS_LABELS[status]}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <Select
          value={typeFilter}
          onChange={(value) => setTypeFilter(value)}
          options={[
            { value: 'customer' as const, label: 'Kundengeräte' },
            { value: 'internal' as const, label: 'Eigene Geräte' },
          ]}
          placeholder="Alle Gerätearten"
        />
        <TextInput value={search} onChange={setSearch} placeholder="Nummer, Gerät, Kunde …" />
      </div>

      <div className="card">
        {isLoading || !data ? (
          <LoadingBlock />
        ) : data.items.length === 0 ? (
          <EmptyState
            title="Keine Reparaturen gefunden."
            hint={'Neue Vorgänge werden über „Gerät annehmen" angelegt.'}
          />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Nummer</th>
                  <th className="shrink">Art</th>
                  <th>Kunde</th>
                  <th>Gerät</th>
                  <th>Fehlerbild</th>
                  <th className="shrink">Angenommen</th>
                  <th className="shrink">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((row) => (
                  <tr key={row.id} onClick={() => setOpenId(row.id)} style={{ cursor: 'pointer' }}>
                    <td className="mono">{row.number}</td>
                    <td>{REPAIR_TYPE_LABELS[row.repairType]}</td>
                    <td>{row.partnerName ?? <span className="faint">—</span>}</td>
                    <td>
                      {row.deviceName}
                      {row.serialNumber ? (
                        <span className="faint" style={{ fontSize: 12 }}> · SN {row.serialNumber}</span>
                      ) : null}
                    </td>
                    <td
                      style={{
                        maxWidth: 280,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.faultDescription}
                    </td>
                    <td>{formatDate(row.receivedDate)}</td>
                    <td>{statusBadge(row.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {creating ? (
        <CreateRepairDialog
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            setOpenId(id);
          }}
        />
      ) : null}
      {openId ? <RepairDetailDialog id={openId} onClose={() => setOpenId(null)} /> : null}
    </>
  );
}

// --- Annahme -----------------------------------------------------------------

function CreateRepairDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [repairType, setRepairType] = useState<RepairType>('customer');
  const [partnerId, setPartnerId] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [deviceNotes, setDeviceNotes] = useState('');
  const [faultDescription, setFaultDescription] = useState('');
  const [receivedDate, setReceivedDate] = useState(new Date().toISOString().slice(0, 10));
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: customers } = useQuery({
    queryKey: ['partners', 'customer', 'all'],
    queryFn: () =>
      api.get<ListResponse<Partner>>(withQuery('/api/partners', { role: 'customer', limit: 500 })),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<{ repair: { id: string } }>('/api/repairs', {
        repairType,
        partnerId: repairType === 'customer' ? partnerId : null,
        deviceName,
        serialNumber: serialNumber || null,
        deviceNotes: deviceNotes || null,
        faultDescription,
        receivedDate,
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['repairs'] });
      toast.success('Das Gerät wurde angenommen.');
      onCreated(result.repair.id);
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setErrors(error.fieldErrors);
        toast.error(error.message);
      } else {
        toast.error('Beim Speichern ist ein Fehler aufgetreten.');
      }
    },
  });

  return (
    <Dialog
      title="Gerät zur Reparatur annehmen"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={
              create.isPending ||
              !deviceName.trim() ||
              !faultDescription.trim() ||
              (repairType === 'customer' && !partnerId)
            }
            onClick={() => create.mutate()}
          >
            Annehmen
          </button>
        </>
      }
    >
      <Field label="Geräteart">
        <Select
          value={repairType}
          onChange={(value) => setRepairType(value)}
          options={[
            { value: 'customer' as const, label: 'Kundengerät' },
            { value: 'internal' as const, label: 'Eigenes Gerät' },
          ]}
        />
      </Field>

      {repairType === 'customer' ? (
        <Field label="Kunde" required error={errors.partnerId}>
          <SearchSelect
            value={partnerId}
            onChange={setPartnerId}
            options={(customers?.items ?? []).map((partner) => ({
              value: partner.id,
              label: `${partner.number} · ${partner.name}`,
            }))}
            placeholder="Kundenname oder -nummer tippen …"
            invalid={Boolean(errors.partnerId)}
          />
        </Field>
      ) : null}

      <div className="row" style={{ gap: 12 }}>
        <Field label="Gerät" required error={errors.deviceName}>
          <TextInput value={deviceName} onChange={setDeviceName} placeholder="z. B. Fräsroboter KA-200" />
        </Field>
        <Field label="Seriennummer" error={errors.serialNumber}>
          <TextInput value={serialNumber} onChange={setSerialNumber} />
        </Field>
        <Field label="Annahmedatum" required error={errors.receivedDate}>
          <TextInput type="date" value={receivedDate} onChange={setReceivedDate} />
        </Field>
      </div>

      <Field label="Fehlerbild" required error={errors.faultDescription}>
        <TextArea value={faultDescription} onChange={setFaultDescription} rows={3} />
      </Field>

      <Field label="Zubehör und Zustand bei Annahme" error={errors.deviceNotes}>
        <TextArea value={deviceNotes} onChange={setDeviceNotes} rows={2} />
      </Field>
    </Dialog>
  );
}

// --- Detail ------------------------------------------------------------------

type LineDraft = {
  key: string;
  itemId: string;
  itemLabel: string;
  description: string;
  quantity: number;
  unitPrice: number;
};

function RepairDetailDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can } = useAuth();
  const writable = can('repairs:write');

  const [drafts, setDrafts] = useState<LineDraft[] | null>(null);
  const [deciding, setDeciding] = useState<'approved' | 'declined' | null>(null);
  const [decisionNote, setDecisionNote] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['repair', id],
    queryFn: () => api.get<RepairDetail>(`/api/repairs/${id}`),
  });

  // Positionen einmal aus dem Server-Stand uebernehmen.
  useEffect(() => {
    if (data && drafts === null) {
      setDrafts(
        data.lines.map((line) => ({
          key: line.id,
          itemId: line.itemId,
          itemLabel: line.description,
          description: line.description,
          quantity: line.quantity,
          unitPrice: line.unitPrice,
        })),
      );
    }
  }, [data, drafts]);

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['repairs'] });
    queryClient.invalidateQueries({ queryKey: ['repair', id] });
  }

  function handlers(message: string) {
    return {
      onSuccess: () => {
        refresh();
        setDeciding(null);
        toast.success(message);
      },
      onError: (error: unknown) =>
        toast.error(error instanceof ApiError ? error.message : 'Die Aktion ist fehlgeschlagen.'),
    };
  }

  const saveLines = useMutation({
    mutationFn: (lines: LineDraft[]) =>
      api.put(`/api/repairs/${id}/lines`, {
        lines: lines.map((line) => ({
          itemId: line.itemId,
          description: line.description || null,
          quantity: line.quantity,
          unitPrice: line.unitPrice,
        })),
      }),
    onSuccess: () => {
      refresh();
      setDrafts(null); // frisch vom Server uebernehmen
      toast.success('Die Positionen wurden gespeichert.');
    },
    onError: (error: unknown) =>
      toast.error(error instanceof ApiError ? error.message : 'Speichern fehlgeschlagen.'),
  });

  const setStatus = useMutation({
    mutationFn: (body: { status: RepairStatus; note?: string | null }) =>
      api.post(`/api/repairs/${id}/status`, body),
    ...handlers('Der Status wurde geändert.'),
  });

  const createQuote = useMutation({
    mutationFn: () => api.post(`/api/repairs/${id}/quote`),
    ...handlers('Der Kostenvoranschlag wurde als Angebotsentwurf angelegt.'),
  });

  const createInvoice = useMutation({
    mutationFn: () => api.post(`/api/repairs/${id}/invoice`),
    ...handlers('Die Rechnung wurde als Entwurf angelegt.'),
  });

  if (isLoading || !data || drafts === null) {
    return (
      <Dialog title="Reparatur" onClose={onClose}>
        <LoadingBlock />
      </Dialog>
    );
  }

  const { repair, partner, quoteDocument, invoiceDocument } = data;
  const isCustomer = repair.repairType === 'customer';
  const linesEditable =
    writable &&
    REPAIR_EDITABLE_STATUSES.includes(repair.status) &&
    !repair.consumptionBooked;

  const draftNet = drafts.reduce(
    (sum, line) => sum + lineAmount(line.quantity, line.unitPrice),
    0,
  );

  const allowedTargets = REPAIR_TRANSITIONS[repair.status].filter((target) => {
    if (!isCustomer && (target === 'quote_sent' || target === 'approved' || target === 'declined'))
      return false;
    // Der Weg zum KV fuehrt ueber "Kostenvoranschlag erstellen", nicht
    // ueber einen nackten Statuswechsel.
    if (target === 'quote_sent') return false;
    return true;
  });

  const statusActionLabel: Partial<Record<RepairStatus, string>> = {
    approved: 'Freigabe erfassen …',
    declined: 'Ablehnung erfassen …',
    in_repair: repair.status === 'completed' ? 'Wieder in Arbeit nehmen' : 'Reparatur beginnen',
    completed: 'Fertig melden',
    closed: repair.status === 'declined' ? 'Abschließen (Gerät zurück)' : 'Abschließen (abgeholt)',
  };

  return (
    <Dialog title={`Reparatur ${repair.number}`} onClose={onClose} wide>
      <div className="row" style={{ gap: 8, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {statusBadge(repair.status)}
        <Badge>{REPAIR_TYPE_LABELS[repair.repairType]}</Badge>
        {repair.consumptionBooked ? <Badge kind="accent">Material gebucht</Badge> : null}
        {quoteDocument ? (
          <Link to={`/verkauf/angebote/${quoteDocument.id}`}>
            KV: Angebot {quoteDocument.number ?? '(Entwurf)'}
          </Link>
        ) : null}
        {invoiceDocument ? (
          <Link to={`/verkauf/rechnungen/${invoiceDocument.id}`}>
            Rechnung {invoiceDocument.number ?? '(Entwurf)'}
          </Link>
        ) : null}
      </div>

      <div className="table-wrap" style={{ marginBottom: 12 }}>
        <table className="data">
          <tbody>
            {isCustomer ? (
              <tr>
                <td className="faint" style={{ width: '11em' }}>Kunde</td>
                <td>
                  {partner ? (
                    <Link to={`/kunden/${partner.id}`}>{partner.number} · {partner.name}</Link>
                  ) : '—'}
                </td>
              </tr>
            ) : null}
            <tr>
              <td className="faint" style={{ width: '11em' }}>Gerät</td>
              <td>
                {repair.deviceName}
                {repair.serialNumber ? ` · SN ${repair.serialNumber}` : ''}
              </td>
            </tr>
            <tr>
              <td className="faint">Fehlerbild</td>
              <td style={{ whiteSpace: 'pre-wrap' }}>{repair.faultDescription}</td>
            </tr>
            {repair.deviceNotes ? (
              <tr>
                <td className="faint">Zubehör/Zustand</td>
                <td style={{ whiteSpace: 'pre-wrap' }}>{repair.deviceNotes}</td>
              </tr>
            ) : null}
            <tr>
              <td className="faint">Angenommen</td>
              <td>{formatDate(repair.receivedDate)}</td>
            </tr>
            {repair.approvalNote ? (
              <tr>
                <td className="faint">Freigabevermerk</td>
                <td style={{ whiteSpace: 'pre-wrap' }}>{repair.approvalNote}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {/* --- Positionen ------------------------------------------------------ */}
      <div className="card" style={{ padding: 12, marginBottom: 12 }}>
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
          <strong>Material und Arbeitszeit</strong>
          {linesEditable ? (
            <div className="row" style={{ gap: 6 }}>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() =>
                  setDrafts((current) => [
                    ...(current ?? []),
                    { key: `neu-${Date.now()}`, itemId: '', itemLabel: '', description: '', quantity: 1000, unitPrice: 0 },
                  ])
                }
              >
                Position hinzufügen
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={saveLines.isPending || drafts.some((line) => !line.itemId)}
                onClick={() => saveLines.mutate(drafts)}
              >
                Positionen speichern
              </button>
            </div>
          ) : null}
        </div>

        {drafts.length === 0 ? (
          <div className="faint" style={{ fontSize: 13 }}>
            Noch keine Positionen. Arbeitszeit ist ein Artikel — z. B. „Standardstundensatz",
            „Garantiestundensatz" oder „Kulanz" als Artikel ohne Bestandsführung anlegen und hier wählen.
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th style={{ minWidth: 220 }}>Artikel / Leistung</th>
                  <th className="numeric" style={{ width: 110 }}>Menge</th>
                  <th className="numeric" style={{ width: 130 }}>Einzelpreis</th>
                  <th className="numeric" style={{ width: 110 }}>Betrag</th>
                  {linesEditable ? <th className="shrink"></th> : null}
                </tr>
              </thead>
              <tbody>
                {drafts.map((line, index) => (
                  <tr key={line.key}>
                    <td>
                      {linesEditable ? (
                        <>
                          <ItemPicker
                            value={line.itemLabel}
                            onSelect={(item) =>
                              setDrafts((current) =>
                                (current ?? []).map((entry, i) =>
                                  i === index
                                    ? {
                                        ...entry,
                                        itemId: item.id,
                                        itemLabel: `${item.number} · ${item.name}`,
                                        description: item.name,
                                        unitPrice: item.salesPrice,
                                      }
                                    : entry,
                                ),
                              )
                            }
                          />
                          <input autoComplete="off"
                            className="input"
                            style={{ marginTop: 4 }}
                            value={line.description}
                            placeholder="Beschreibung auf KV und Rechnung"
                            onChange={(event) =>
                              setDrafts((current) =>
                                (current ?? []).map((entry, i) =>
                                  i === index ? { ...entry, description: event.target.value } : entry,
                                ),
                              )
                            }
                          />
                        </>
                      ) : (
                        line.description
                      )}
                    </td>
                    <td className="numeric">
                      {linesEditable ? (
                        <NumericInput
                          kind="quantity"
                          value={line.quantity}
                          onChange={(value) =>
                            setDrafts((current) =>
                              (current ?? []).map((entry, i) =>
                                i === index ? { ...entry, quantity: value } : entry,
                              ),
                            )
                          }
                        />
                      ) : (
                        formatQuantity(line.quantity)
                      )}
                    </td>
                    <td className="numeric">
                      {linesEditable ? (
                        <NumericInput
                          kind="price"
                          value={line.unitPrice}
                          onChange={(value) =>
                            setDrafts((current) =>
                              (current ?? []).map((entry, i) =>
                                i === index ? { ...entry, unitPrice: value } : entry,
                              ),
                            )
                          }
                        />
                      ) : (
                        formatAmount(lineAmount(line.quantity, line.unitPrice))
                      )}
                    </td>
                    <td className="numeric mono">
                      {formatAmount(lineAmount(line.quantity, line.unitPrice))} €
                    </td>
                    {linesEditable ? (
                      <td>
                        <button
                          type="button"
                          className="btn btn-quiet btn-sm"
                          onClick={() =>
                            setDrafts((current) => (current ?? []).filter((_, i) => i !== index))
                          }
                        >
                          ✕
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3} style={{ textAlign: 'right', fontWeight: 500 }}>
                    Summe netto
                  </td>
                  <td className="numeric mono" style={{ fontWeight: 600 }}>
                    {formatAmount(draftNet)} €
                  </td>
                  {linesEditable ? <td></td> : null}
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {!linesEditable && repair.consumptionBooked ? (
          <div className="faint" style={{ fontSize: 12.5, marginTop: 6 }}>
            Nach der Fertigmeldung sind die Positionen festgeschrieben — das Material ist als
            Lagerabgang gebucht.
          </div>
        ) : null}
      </div>

      {/* --- Freigabe / Ablehnung -------------------------------------------- */}
      {writable && deciding ? (
        <div className="card" style={{ padding: 12, marginBottom: 12 }}>
          <Alert kind={deciding === 'approved' ? 'info' : 'warning'}>
            {deciding === 'approved'
              ? 'Freigabe des Kostenvoranschlags durch den Kunden festhalten (wer, wann, wie).'
              : 'Ablehnung festhalten — das Gerät geht danach unrepariert zurück.'}
          </Alert>
          <Field label="Vermerk">
            <TextArea value={decisionNote} onChange={setDecisionNote} rows={2} />
          </Field>
          <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-sm" onClick={() => setDeciding(null)}>
              Abbrechen
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={setStatus.isPending}
              onClick={() => setStatus.mutate({ status: deciding, note: decisionNote || null })}
            >
              {deciding === 'approved' ? 'Freigabe erfassen' : 'Ablehnung erfassen'}
            </button>
          </div>
        </div>
      ) : null}

      {/* --- Aktionen --------------------------------------------------------- */}
      {writable && !deciding ? (
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          {isCustomer && !repair.quoteDocumentId && drafts.length > 0 ? (
            <button
              type="button"
              className="btn btn-sm"
              disabled={createQuote.isPending}
              onClick={() => createQuote.mutate()}
              title="Erstellt ein Angebot mit den Positionen — Druck und Versand laufen über den Beleg"
            >
              Kostenvoranschlag erstellen
            </button>
          ) : null}

          {allowedTargets.map((target) =>
            target === 'approved' || target === 'declined' ? (
              <button
                key={target}
                type="button"
                className="btn btn-sm"
                onClick={() => {
                  setDecisionNote('');
                  setDeciding(target);
                }}
              >
                {statusActionLabel[target]}
              </button>
            ) : (
              <button
                key={target}
                type="button"
                className={`btn btn-sm${target === 'completed' ? ' btn-primary' : ''}`}
                disabled={setStatus.isPending}
                title={
                  target === 'completed'
                    ? 'Bucht das Material der Positionen als Lagerabgang'
                    : undefined
                }
                onClick={() => setStatus.mutate({ status: target })}
              >
                {statusActionLabel[target]}
              </button>
            ),
          )}

          {isCustomer &&
          (repair.status === 'completed' || repair.status === 'closed') &&
          !repair.invoiceDocumentId ? (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={createInvoice.isPending}
              onClick={() => createInvoice.mutate()}
            >
              Rechnung erstellen
            </button>
          ) : null}
        </div>
      ) : null}
    </Dialog>
  );
}
